import type { ReactNode } from 'react'
import { cn } from '../lib/cn.ts'

// From md up the wide table stays as it is; below it the same rows come as a list of cards,
// so a phone never scrolls sideways. Both are rendered and CSS picks one — no layout jump on load.

/** Spacing and type size of a card; reused by cards that are a link as a whole. */
export const mobileCardClass = 'flex flex-col gap-3 px-4 py-4 text-[13.5px]'

export function DesktopTable({ children }: { children: ReactNode }) {
  return <div className="hidden overflow-x-auto md:block">{children}</div>
}

export function MobileCardList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <ul aria-label={label} className="divide-y divide-line-soft md:hidden">
      {children}
    </ul>
  )
}

export function MobileCard({ children, className }: { children: ReactNode; className?: string }) {
  return <li className={cn(mobileCardClass, className)}>{children}</li>
}

/** First line of a card: the title on the left, its status on the right. */
export function CardHeading({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">{children}</div>
      {aside && <div className="flex shrink-0 flex-col items-end gap-1 pt-0.5">{aside}</div>}
    </div>
  )
}

/** Labelled details in two columns; a `wide` fact takes the whole row. */
export function CardFacts({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</dl>
}

interface CardFactProps {
  label: string
  children: ReactNode
  wide?: boolean
}

export function CardFact({ label, children, wide = false }: CardFactProps) {
  return (
    <div className={cn('min-w-0', wide && 'col-span-2')}>
      <dt className="text-[12px] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 min-w-0">{children}</dd>
    </div>
  )
}

/** Counts on one wrapping line: «Услуг 34  Клиентов 412». */
export function CardStats({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-x-4 gap-y-1">{children}</div>
}

export function CardStat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="whitespace-nowrap">
      <span className="text-ink-muted">{label}</span> <span className="font-medium">{children}</span>
    </span>
  )
}

/** Card buttons share the width evenly and stay at least 44px tall. */
export function CardActions({ children }: { children: ReactNode }) {
  return <div className="grid auto-cols-fr grid-flow-col gap-2 [&>button]:min-h-11">{children}</div>
}
